import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import MuiSelect from '@mui/material/Select';
import type { SelectChangeEvent, SelectProps } from '@mui/material/Select';
import { useId, type ReactNode } from 'react';

export type AppSelectProps<T = string> = Omit<SelectProps<T>, 'variant' | 'label'> & {
  label?: ReactNode;
  helperText?: ReactNode;
  fullWidth?: boolean;
};

/**
 * App Select: Material 3 outlined FormControl + floating label + helper/error.
 */
export function Select<T = string>({
  label,
  helperText,
  error,
  disabled,
  required,
  fullWidth,
  size = 'medium',
  sx,
  children,
  displayEmpty = false,
  ...props
}: AppSelectProps<T>) {
  const uid = useId();
  const labelId = `${uid}-label`;
  const helperId = `${uid}-helper`;

  return (
    <FormControl
      variant="outlined"
      fullWidth={fullWidth}
      error={error}
      disabled={disabled}
      required={required}
      size={size}
      sx={sx}
    >
      {label ? <InputLabel id={labelId}>{label}</InputLabel> : null}
      <MuiSelect
        labelId={label ? labelId : undefined}
        label={label}
        displayEmpty={displayEmpty}
        aria-describedby={helperText ? helperId : undefined}
        error={error}
        {...props}
      >
        {children}
      </MuiSelect>
      {helperText ? <FormHelperText id={helperId}>{helperText}</FormHelperText> : null}
    </FormControl>
  );
}

export { MenuItem };
export type { SelectChangeEvent, SelectProps };
